import React, { useEffect, useState } from 'react';
import { CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import QuickBooksService from '../services/quickbooksService';
import AdminService from '../services/adminService';

const QuickBooksCallback = () => {
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Processing QuickBooks authorization...');

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    try {
      // Get URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const realmId = urlParams.get('realmId');
      const error = urlParams.get('error');

      // Check for OAuth errors
      if (error) {
        setStatus('error');
        setMessage(`OAuth error: ${error}`);
        return;
      }

      if (!code || !state || !realmId) {
        setStatus('error');
        setMessage('Missing required parameters from QuickBooks');
        return;
      }

      // If we're in the ngrok popup, get the state from the parent window via postMessage
      let storedState, storedFranchiseeId;
      
      if (window.opener && window.location.hostname.includes('ngrok')) {
        // We're in an ngrok popup, get localStorage from parent window via postMessage
        try {
          // Send message to parent asking for OAuth state
          window.opener.postMessage({ 
            type: 'GET_OAUTH_STATE', 
            code, 
            state, 
            realmId 
          }, 'http://localhost:3000');
          
          // Close popup - parent will handle the OAuth completion
          window.close();
          return;
        } catch (error) {
          console.error('Cannot communicate with parent window:', error);
          setStatus('error');
          setMessage('Cannot verify OAuth state - popup communication failed');
          return;
        }
      } else {
        // Normal flow - get from our own localStorage
        storedState = localStorage.getItem('qb_oauth_state');
        storedFranchiseeId = localStorage.getItem('qb_franchisee_id');
      }
      
      if (!storedState || state !== storedState) {
        setStatus('error');
        setMessage('Invalid state parameter - possible CSRF attack');
        return;
      }

      if (!storedFranchiseeId) {
        setStatus('error');
        setMessage('Franchisee ID not found in local storage');
        return;
      }

      setMessage('Exchanging authorization code for tokens...');

      // Exchange code for tokens
      const tokenInfo = await QuickBooksService.exchangeCodeForToken(code, state, realmId);
      
      setMessage('Saving connection information...');

      // Save the connection info to MySQL database
      await QuickBooksService.saveConnectionInfo(storedFranchiseeId, tokenInfo);

      setStatus('success');
      setMessage('QuickBooks successfully connected! You can close this window.');

      // Clean up localStorage (from parent window if we're in ngrok popup)
      if (window.opener && window.location.hostname.includes('ngrok')) {
        try {
          window.opener.localStorage.removeItem('qb_oauth_state');
          window.opener.localStorage.removeItem('qb_franchisee_id');
        } catch (error) {
          console.warn('Could not clean up parent localStorage:', error);
        }
      } else {
        localStorage.removeItem('qb_oauth_state');
        localStorage.removeItem('qb_franchisee_id');
      }

      // Stop ngrok since OAuth is complete
      try {
        await AdminService.stopNgrok();
        console.log('ngrok stopped after successful OAuth');
      } catch (ngrokError) {
        console.warn('Could not stop ngrok:', ngrokError.message);
        // Don't fail the OAuth process if ngrok stop fails
      }

      // If accessed via ngrok, redirect to localhost and close
      if (window.location.hostname.includes('ngrok')) {
        // Redirect parent window to localhost if opened in popup
        if (window.opener) {
          window.opener.focus();
        }
        // Close the popup after success
        setTimeout(() => {
          window.close();
        }, 2000);
      } else {
        // Close the popup after success
        setTimeout(() => {
          window.close();
        }, 3000);
      }

    } catch (error) {
      console.error('Error in callback handler:', error);
      setStatus('error');
      setMessage(`Error: ${error.message || 'Unknown error occurred'}`);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            QuickBooks Integration
          </h2>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-center space-y-4">
            {status === 'processing' && (
              <>
                <RefreshCw className="mx-auto animate-spin text-pest-blue" size={48} />
                <p className="text-gray-700">{message}</p>
              </>
            )}
            
            {status === 'success' && (
              <>
                <CheckCircle className="mx-auto text-green-500" size={48} />
                <p className="text-green-700 font-semibold">{message}</p>
                <p className="text-sm text-gray-500">This window will close automatically...</p>
              </>
            )}
            
            {status === 'error' && (
              <>
                <AlertCircle className="mx-auto text-red-500" size={48} />
                <p className="text-red-700 font-semibold">Connection Failed</p>
                <p className="text-gray-600">{message}</p>
                <button
                  onClick={() => window.close()}
                  className="mt-4 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
                >
                  Close Window
                </button>
              </>
            )}
          </div>
        </div>

        <div className="text-center">
          <p className="text-sm text-gray-500">
            If this window doesn't close automatically, you can safely close it manually.
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuickBooksCallback;